import { computed } from "vue";
import { useStore } from "vuex";
import { hasAnyPermission } from "@/components/Header/utils.js";
import { API_RELATIONSHIPS, FEATURES, PERMISSIONS } from "@/constants.js";
import store from "@/store/index.js";

export const useAuthorization = () => {
  const vStore = useStore();

  const userPermissions = computed(() => {
    return vStore.getters.getPermissions;
  });

  const isStudent = computed(() => userPermissions.value.includes(PERMISSIONS.PARTICIPANTS));

  return {
    isStudent,
  };
};

export const featureFlags = computed(() => {
  return store.getters.getFeatureFlags;
});

const currentUserAPIRelationship = computed(() => {
  return store.getters.getEntityApiRelationship;
});

const currentUserPermissions = computed(() => {
  return store.getters.getPermissions ?? [];
});

export const isAdmin = () => currentUserPermissions.value.includes(PERMISSIONS.SUPER_ADMIN);

export const isApiEmployee = () => currentUserPermissions.value.includes(PERMISSIONS.API_EMPLOYEE);

export const isAdvisor = () => currentUserPermissions.value.includes(PERMISSIONS.INSTITUTION);

export const isStudent = () => currentUserPermissions.value.includes(PERMISSIONS.PARTICIPANTS);

export const isAdvisorOrEmployee = () => isAdvisor() || isApiEmployee();

export const isTenantManager = () => currentUserPermissions.value.includes(PERMISSIONS.TENANT_MANAGER);

export const hasPublishPermission = () => isAuthorized(PERMISSIONS.ITEMS_PUB);
export const hasWritePermission = () => isAuthorized(PERMISSIONS.ITEMS_WRITE);

export const hasProgramManagerAppPermission = () =>
  isAuthorized([
    PERMISSIONS.ITEMS_READ,
    PERMISSIONS.ITEMS_PUB,
    PERMISSIONS.PRGMS_PUB,
    PERMISSIONS.PRGMS_READ,
    PERMISSIONS.PRGMS_WRITE,
    PERMISSIONS.ITEMS_WRITE,
  ]);

export const hasProgramsReadLitePermission = () =>
  isAuthorized([PERMISSIONS.PRGMS_PUB, PERMISSIONS.PRGMS_WRITE, PERMISSIONS.PRGMS_READ, PERMISSIONS.PRGMS_READ_LITE]);

export const hasItemsPermissionProgramManager = () =>
  isAuthorized([PERMISSIONS.ITEMS_PUB, PERMISSIONS.ITEMS_WRITE, PERMISSIONS.ITEMS_READ, PERMISSIONS.ITEMS_READ_LITE]);

export const hasProgramsReadPermission = () =>
  isAuthorized([PERMISSIONS.PRGMS_PUB, PERMISSIONS.PRGMS_WRITE, PERMISSIONS.PRGMS_READ]);

export const hasProgramsWritePermission = () => isAuthorized([PERMISSIONS.PRGMS_PUB, PERMISSIONS.PRGMS_WRITE]);

export const hasProgramsPublishPermission = () => isAuthorized([PERMISSIONS.PRGMS_PUB]);

/**
 * Whether the current user may edit session application fee / confirmation deposit fields.
 * Allowed when:
 * - hasProgramsPublishPermission any session status; or
 * - User has prgms.write on their permission list: Draft only.
 *
 * @param {object} params
 * @param {boolean} params.isSessionOwner
 * @param {number|undefined|null} params.sessionStatusId
 * @param {number} params.draftStatusId
 * @returns {boolean}
 */
export const canEditSessionConfigurableFees = ({ isSessionOwner, sessionStatusId, draftStatusId }) => {
  if (!isSessionOwner) {
    return false;
  }
  if (hasProgramsPublishPermission()) {
    return true;
  }
  const permissions = store?.getters.getPermissions ?? [];
  const isDraft = sessionStatusId === draftStatusId;
  return isDraft && permissions.includes(PERMISSIONS.PRGMS_WRITE);
};

export const isAuthorized = (requiredPermissions) => {
  if (!Array.isArray(requiredPermissions)) {
    requiredPermissions = [requiredPermissions];
  }

  if (requiredPermissions.length < 1) {
    return true;
  }

  return isAdmin() || currentUserPermissions.value.some((permission) => requiredPermissions.includes(permission));
};

export const canRegisterForPrograms = computed(() => {
  return isStudent() && !(isAdvisor() || isApiEmployee());
});

export const hasItemsWritePermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ITEMS_WRITE);
});

export const hasItemsWriteOrItemsPubPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ITEMS_WRITE) || permissions?.includes(PERMISSIONS.ITEMS_PUB);
});

export const hasItemsReadPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ITEMS_READ);
});

export const hasItemsReadLitePermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ITEMS_READ_LITE);
});

export const hasAPIEmployeePermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.API_EMPLOYEE);
});

export const hasDiscountPricingPermission = computed(() => isAuthorized([PERMISSIONS.TENANT_MANAGER]));

export const researchAppEnabled = computed(() => {
  // Delete this computed property when flag gets deprecated
  return featureFlags.value?.[PERMISSIONS.ENABLE_RESEARCH_APP];
});

export const homeAppEnabled = computed(() => {
  // Delete this computed property when flag gets deprecated
  return featureFlags.value?.[PERMISSIONS.ENABLE_HOME_APP];
});

export const careerAndSkillsAppEnabled = computed(() => {
  // Delete this computed property when flag gets deprecated
  return featureFlags.value?.[PERMISSIONS.ENABLE_CAREER_AND_SKILLS_APP];
});

export const hasCacheFeatureEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.cacheClear] && hasAPIEmployeePermission.value;
});

export const hasInvoiceFeatureEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.invoice];
});

export const hasMyStudentsToDosEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.myStudentsToDos];
});

export const hasMyStudentsRetiredEnabled = computed(() => {
  return featureFlags.value[FEATURES.myStudentsRetired];
});

export const hasStripeApplicationDepositEnabled = computed(() => {
  return featureFlags.value[FEATURES.stripeApplicationDeposit];
});

export const hasOrdersReadPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ORDERS_READ);
});

export const hasStudentFulfillmentPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  const hasOrdersOrStudentView =
    permissions?.includes(PERMISSIONS.API_EMPLOYEE_STUDENT_VIEW) || permissions?.includes(PERMISSIONS.ORDERS_READ);

  return hasOrdersOrStudentView;
});

export const hasOrdersWritePermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ORDERS_WRITE);
});

export const hasOrdersAdminPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ORDERS_ADMIN);
});

export const hasUsersAdvisePermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.USERS_ADVISE);
});
export const hasUsersWritePermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.USERS_WRITE);
});

// LINE ITEMS PERMISSIONS
export const hasOrdersWriteEvents = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ORDERS_WRITE_EVENTS) || isAdmin();
});
export const hasOrdersWriteMiscProducts = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ORDERS_WRITE_MISC_PRODUCTS) || isAdmin();
});
export const hasOrdersWriteInternships = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ORDERS_WRITE_INTERNSHIPS) || isAdmin();
});
export const hasOrdersWriteRooms = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ORDERS_WRITE_ROOMS) || isAdmin();
});
export const hasOrdersWriteAdminPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ORDERS_WRITE_ADMIN) || isAdmin();
});

export const hasOrdersWriteFulfillPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.ORDERS_WRITE_FULFILL) || isAdmin();
});

export const hasStudentViewPermission = computed(() => {
  const permissions = store.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.INSTITUTION) || permissions?.includes(PERMISSIONS.STUDENT_MANAGEMENT);
});

export const advisorHasTableauMemberPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.INSTITUTION) && permissions?.includes(PERMISSIONS.TABLEAU_VIEWER_MEMBER);
});

export const hasAdvisorInstitutionPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.INSTITUTION);
});

export const hasAnyTableauLevelPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return (
    permissions?.includes(PERMISSIONS.TABLEAU) ||
    permissions?.includes(PERMISSIONS.TABLEAU_CREATOR) ||
    permissions?.includes(PERMISSIONS.TABLEAU_EXPLORER) ||
    permissions?.includes(PERMISSIONS.TABLEAU_VIEWER) ||
    permissions?.includes(PERMISSIONS.TABLEAU_VIEWER_EMSA) ||
    permissions?.includes(PERMISSIONS.TABLEAU_VIEWER_MEMBER)
  );
});

export const hasPriceOverrideApproverPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  return permissions?.includes(PERMISSIONS.PRICE_OVERRIDE_APPROVER);
});

export const isExclusivelyParticipant = computed(() => {
  const userTypes = store?.state?.profileData?.user_types;
  return userTypes?.length === 1 && userTypes[0].value === "Participant";
});

export const isParticipantOrIsImpersonating = computed(() => {
  return store.getters.isImpersonating || isStudent();
});

export const isTenantLibrariesFlagEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.tenantLibraries];
});

export const employeeHasAnyTableauPermission = computed(() => {
  const permissions = store?.getters.getPermissions;
  const tableauPermissions = [
    PERMISSIONS.TABLEAU,
    PERMISSIONS.TABLEAU_CREATOR,
    PERMISSIONS.TABLEAU_EXPLORER,
    PERMISSIONS.TABLEAU_VIEWER,
    PERMISSIONS.TABLEAU_VIEWER_EMSA,
  ];

  return (
    permissions?.includes(PERMISSIONS.API_EMPLOYEE) &&
    permissions?.some((userPermission) => tableauPermissions.includes(userPermission))
  );
});

/**
 * Checks if the current user is the owner of a library item.
 * @param {string} entityId - The ID of the entity to check ownership for.
 * @returns {boolean} True if the user is the owner of the library item, has API employee permissions,
 * or the feature flag is disabled, false otherwise.
 * NB: Should be refactored to remove isTenantLibrariesEnabled condition as soon the feature flag is removed
 */
export const isLibraryItemOwner = (entityId) => {
  if (isTenantManager()) {
    return true;
  }
  const userEntities = store?.getters.getEntityIds;

  return isTenantLibrariesFlagEnabled.value ? userEntities.some((entity) => entity === entityId) : true;
};

/**
 * Sets marketplace to true.
 * @returns {boolean} True if the user is not a tenant manager or the feature flag is disabled, false otherwise
 * NB: Should be refactored to remove isTenantLibrariesEnabled condition as soon the feature flag is removed
 */
export const shouldAddMarketplace = () => {
  return !isTenantManager() && !isTenantLibrariesFlagEnabled.value;
};

/**
 * Maintaining tableau naming for legacy backwards compatability/readability
 * Core premium app access logic without feature flag checks
 * @returns {boolean} True if user has the necessary permissions for premium membership only apps
 */
export const checkPremiumAppAccess = () => {
  if (hasAPIEmployeePermission.value && employeeHasAnyTableauPermission.value) {
    return true;
  } else if (advisorHasTableauMemberPermission.value) {
    return currentUserAPIRelationship.value === API_RELATIONSHIPS.APIC_MEMBER;
  }
  return false;
};

/**
 * Checks if user has access to a tableau application
 * @param {string} applicationName - The launch darkly name of the application, names are in kebab-case and should be in constants.js
 * @returns {boolean} True if user can view icon in sidebar and access the route, otherwise false and block him from entering.
 * Notes: Used as a route navigation guard and inside a computed property
 */
export function checkTableauAppAccess(applicationName = "") {
  // Delete this when all flags are deprecated
  let applicationIsActive = false;

  // Delete this statement when all flags are deprecated
  switch (applicationName) {
    case PERMISSIONS.ENABLE_RESEARCH_APP:
      applicationIsActive = researchAppEnabled.value;
      break;
    case PERMISSIONS.ENABLE_CAREER_AND_SKILLS_APP:
      applicationIsActive = careerAndSkillsAppEnabled.value;
      break;
    case PERMISSIONS.ENABLE_HOME_APP:
      applicationIsActive = homeAppEnabled.value;
      break;
    case PERMISSIONS.ENABLE_MY_STUDENTS_APP:
      applicationIsActive = myStudentsAppEnabled.value;
      break;
  }

  if (!applicationIsActive) {
    return false; // Delete condition if all flags are deprecated.
  }

  return checkPremiumAppAccess();
}

export const hasAccessToCurriculumIntegration = computed(() => checkPremiumAppAccess());

export const hasAccessToResearchApp = computed(() => checkTableauAppAccess(PERMISSIONS.ENABLE_RESEARCH_APP));

export const hasAccessToCareerAndSkillsApp = computed(() =>
  checkTableauAppAccess(PERMISSIONS.ENABLE_CAREER_AND_SKILLS_APP),
);

export const hasAccessToHomeApp = computed(() => checkTableauAppAccess(PERMISSIONS.ENABLE_HOME_APP));

export const userIsApprovalStudentManager = computed(() =>
  hasAnyPermission(store.getters.getPermissions, PERMISSIONS.APPROVAL),
);

export const userIsAcceptanceStudentManager = computed(() =>
  hasAnyPermission(store.getters.getPermissions, PERMISSIONS.ACCEPTANCE),
);

export const hasTodoDowloadSubmissionDocPermission = computed(
  () => store.getters.getPermissions?.includes(PERMISSIONS.TODO_DOWNLOAD_SUBMISSION_DOC) || isAdmin(),
);

export const hasTodoViewSubmissionPermission = computed(
  () => store.getters.getPermissions?.includes(PERMISSIONS.TODO_VIEW_SUBMISSION) || isAdmin(),
);

export const hasTodoUpdateSubmissionPermission = computed(
  () => store.getters.getPermissions?.includes(PERMISSIONS.TODO_UPDATE_SUBMISSION) || isAdmin(),
);

export const hasTodoAddPermission = computed(
  () => store.getters.getPermissions?.includes(PERMISSIONS.TODO_ADD) || isAdmin(),
);

export const hasEnableShareOptionsEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.enableShareOptions];
});

export const hasGlobalLibrariesEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.globalLibraries];
});

export const hasMFAFormsEnabled = computed(() => {
  return store.getters.getBooleanFeatureFlag("mfa-forms") ?? false;
});

export const hasMyExperiencePageEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.myExperiencePage];
});

export const hasMyExperiencesConnectAiqEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.myExperiencesConnectAiq];
});

export const hasExperienceDetailsPageEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.experienceDetailsPage];
});

export const hasConfigurableRegistrationEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.configurableRegistration];
});

export const hasConfigurableCancellationPolicyEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.showConfigurableCancellationPolicy];
});

export const showNewExcursionConfiguratorDesign = computed(() => {
  return featureFlags.value?.[FEATURES.showNewExcursionConfiguratorDesign];
});

export const showExcursionOccurrenceDates = computed(() => {
  return featureFlags.value?.[FEATURES.showExcursionOccurrenceDates];
});

export const hasScholarshipElegiblityEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.scholarshipElegiblity];
});

export const hasCourseApprover = computed(() => {
  return store.getters.getPermissions?.includes(PERMISSIONS.COURSE_APPROVER);
});

export const hasShowGlobalTranscriptReleaseFormEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.showGlobalTranscriptReleaseForm];
});

export const hasLegacyEnrollmentTimeline = computed(() => {
  return featureFlags.value?.[FEATURES.legacyEnrollmentPageTimeline];
});

export const hasLegacyEnrollmentPageRemindersSection = computed(() => {
  return featureFlags.value?.[FEATURES.legacyEnrollmentPageRemindersSection];
});

export const hasShowSessionExternalLinkInformation = computed(() => {
  return featureFlags.value?.[FEATURES.legacyEnrollmentPageRemindersSection];
});

export const usePriceOverrides = computed(() => featureFlags.value?.[FEATURES.priceOverrides]);

export const useCustomPriceFees = computed(() => featureFlags.value?.[FEATURES.useCustomPriceFees]);

export const hasAcceptanceStatusFilterEnabled = computed(() => featureFlags.value?.[FEATURES.acceptanceStatusFilter]);

export const hasPriceOverrideApprovalsAccess = computed(
  () => hasPriceOverrideApproverPermission.value || hasOrdersWritePermission.value,
);

export const hasHiddenNavItemsEnabled = computed(() => {
  const urlParams = new URLSearchParams(window.location.search);
  return featureFlags.value?.[FEATURES.showHiddenNavItems] || urlParams.has("showHiddenNavItems");
});

export const onboardingTodosReportV2FeatureFlag = computed(() => {
  return featureFlags.value?.[FEATURES.onboardingTodosReportV2];
});

export const hasTravelPlansForceMobileAdoptionEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.travelPlansForceMobileAdoption];
});

export const hasEnableContentMangementEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.enableContentManagement];
});

export const hasShowTenantPaymentProcessingEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.showTenantPaymentProcessing];
});

export const hasAiAdvisingChatbotEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.aiAdvisingChatbot];
});

export const hasShowStudentCommunicationsEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.showStudentCommunications];
});

export const hasTodosInboxReportEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.todosInboxReport];
});

export const hasTodosSubmissionsReportEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.todosSubmissionsReport];
});

export const hasDisplayStudentDiscountEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.displayStudentDiscount];
});

export const hasDisplayAdminUIForStudentDiscountsEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.displayAdminUIForStudentDiscounts];
});

export const hasDisplayCustomPricingWriteActionsEnabled = computed(() => {
  return featureFlags.value?.[FEATURES.displayCustomPricingWriteActions];
});

export const showAddOnsInFulfillment = computed(() => {
  return featureFlags.value?.[FEATURES.showAddOnsInFulfillment];
});
